import styled from 'styled-components';

import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import {
  CollapsibleContentArrow,
  ARROW_DIRECTION_UP,
  ARROW_DIRECTION_DOWN,
} from 'ui/collapsibleContent';
import { ENTER_KEY, SPACE_KEY } from 'constants/keyCodes';
import { Radio } from 'ui/radio';
import noop from 'utils/noop';
import { colors, spacing } from 'ui/theme';

import { DoubleLine } from '../Selects.styles';
import {
  StyledWrapper,
  StyledTrigger as Trigger,
  ClickableContent,
  StyledCount,
  StyledContent,
  ArrowContainer,
} from '../ExpandableCategoryGroup';
import { ExpandableCategoryProps } from '../types';

type Props = ExpandableCategoryProps & {
  countText?: { plural: string; single: string };
  categoryInfo: { id: string; doubleLine?: string };
  isSelected?: boolean;
};

const StyledTrigger = styled(Trigger)`
  border: solid ${spacing.single} transparent;
  border-radius: ${spacing.quarter};
  margin-bottom: ${spacing.single}; /** keeps focus state from causing overlaps */
  overflow: hidden;
  outline: none;

  :hover {
    background: ${colors.grey5};
  }

  :focus {
    border-color: ${colors.purple50};
    box-shadow: 0px 0px 0px ${spacing.single} ${colors.purple50};
  }
`;

export const RadioExpandableCategoryGroup = ({
  categoryInfo,
  count,
  countText,
  children,
  isSelected,
  label,
  onClick = noop,
}: Props) => {
  // This isnt a great solution but right now every time something is selected this component is remounted so to persist state storing it locally
  // If we pull the isCollapsed up a level the collapse component doesnt animate
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    `${categoryInfo?.id}-${label}-isCollapsed`,
    false,
  );

  const isSingle = count && count === 1;
  const countString = isSingle ? countText?.single : countText?.plural;
  const countLabel = countText ? `${count} ${countString}` : count;

  return (
    <StyledWrapper>
      <StyledTrigger
        tabIndex={0}
        onKeyDown={({ keyCode }: { keyCode: number }) => {
          if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
            onClick();
          }
        }}
      >
        <Radio
          isChecked={isSelected || false}
          size={20}
          id="Radio-Option"
          onClick={onClick}
          value=""
          tabIndex={-1}
        />
        <ClickableContent>
          <div onClick={onClick}>
            <div>{label}</div>
            {categoryInfo.doubleLine && <DoubleLine>{categoryInfo.doubleLine}</DoubleLine>}
          </div>
          <div onClick={() => setIsCollapsed(!isCollapsed)}>
            <ArrowContainer>
              <StyledCount>{countLabel}</StyledCount>
              <CollapsibleContentArrow
                width={16}
                height={16}
                isCollapsed={isCollapsed}
                arrowStart={ARROW_DIRECTION_DOWN}
                arrowEnd={ARROW_DIRECTION_UP}
              />
            </ArrowContainer>
          </div>
        </ClickableContent>
      </StyledTrigger>
      <StyledContent isCollapsed={isCollapsed}>{children}</StyledContent>
    </StyledWrapper>
  );
};
